/**
 * PrescriptionCart Component
 *
 * A comprehensive shopping cart and checkout component for patient-facing payment flows.
 * This component is used when patients access payment links sent by practitioners.
 *
 * Features:
 * - Displays prescription medications with pricing
 * - Shows pre-configured shipping information (selected by practitioner)
 * - Displays shipping cost or free shipping status
 * - Handles payment processing via Stripe payment gateway
 * - Supports both one-time and subscription payments
 * - Real-time form validation
 *
 * Shipping Display:
 * When a practitioner sends a payment link, they pre-select the shipping carrier
 * and delivery option. The patient sees:
 * - Carrier name and service type
 * - Shipping cost (or $0 if free shipment applies)
 * - Original cost with strikethrough if free shipment
 * - "Free shipping applied!" badge when applicable
 *
 * The shipping cost is automatically included in the total amount
 * and is directed to NovaMed (not the clinic) during split payment processing.
 *
 * @module PrescriptionCart
 */

import React, { useState, useEffect, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  ArrowLeft,
  Package,
  CreditCard,
  User,
  AlertCircle,
  Loader2,
  ShieldCheck,
  Receipt,
  Plus,
  Minus,
  CheckCircle,
  Lock,
  RefreshCw,
  Calendar,
  Truck,
  Gift,
  MapPin,
  Pencil,
  Snowflake,
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Separator } from '@/components/ui/separator';
import { Switch } from '@/components/ui/switch';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { useNavigate, useParams } from 'react-router-dom';
import { toast } from '@/hooks/use-toast';
import { usePrescriptionCart, PrescriptionCartItem } from '@/hooks/usePrescriptionCart';
import { useClinic } from '@/contexts/ClinicContext';
import { Patient } from '@/lib/types';
import { medicationUnits } from '@/lib/medication-unit';
import { ShippingCarrier, ShippingOption, ShippingSelection } from '@/lib/types';
import { prescriptionService } from '@/services/prescriptionService';
import {
  processPayment,
  isStripeOnboardingRequiredError,
  STRIPE_ONBOARDING_TOAST,
} from '@/services/paymentService'
import {
  shippingService,
  prescriptionMedicationRequestsToShippingLines,
  type PackageDimensions,
} from '@/services/shippingService';
import { useQuery } from '@tanstack/react-query';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { StripePaymentForm } from '@/components/payments/StripePaymentForm'
import { getStripeInstance } from '@/lib/stripe-loader'
import { ToastAction } from '@/components/ui/toast'
import { formatDOB } from '@/lib/dateUtils';
import AddressInput, { type AddressData } from '@/components/ui/AddressInput';

// Checkout Steps
enum CheckoutStep {
  REVIEW = 'review',
  PAYMENT = 'payment',
  PROCESSING = 'processing',
  SUCCESS = 'success',
  ERROR = 'error',
}

interface PrescriptionCartProps {
  patient: Patient;
  onBack?: () => void;
}

// Skeleton Loader Component
const PrescriptionItemSkeleton: React.FC = () => (
  <Card className='border-neutral-200'>
    <CardContent className='p-4'>
      <div className='flex animate-pulse items-center justify-between'>
        <div className='flex-1'>
          <div className='mb-2 h-5 w-48 rounded bg-neutral-200 dark:bg-neutral-700'></div>
          <div className='mb-2 h-4 w-32 rounded bg-neutral-200 dark:bg-neutral-700'></div>
          <div className='h-4 w-24 rounded bg-neutral-200 dark:bg-neutral-700'></div>
        </div>
        <div className='flex flex-col items-end space-y-2'>
          <div className='h-8 w-16 rounded bg-neutral-200 dark:bg-neutral-700'></div>
          <div className='h-8 w-24 rounded bg-neutral-200 dark:bg-neutral-700'></div>
        </div>
      </div>
    </CardContent>
  </Card>
);

// Prescription Item Component
const PrescriptionItemCard: React.FC<{ item: PrescriptionCartItem; index: number }> = ({
  item,
  index,
}) => {
  const { updateItemQuantity } = usePrescriptionCart();

  const handleQuantityChange = (change: number) => {
    const newQuantity = item.quantity_authorized + change;
    if (newQuantity > 0) {
      updateItemQuantity(item.id, newQuantity);
    }
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.3, delay: index * 0.1 }}
      layout
    >
      <Card className='border-neutral-200 transition-shadow hover:shadow-md'>
        <CardContent className='p-4'>
          <div className='flex items-center justify-between'>
            <div className='flex-1'>
              <div className='mb-2 flex items-center space-x-3'>
                <h4 className='font-semibold text-neutral-900 dark:text-neutral-100'>
                  {item.medication_name || item.medication}
                </h4>
                <Badge variant='outline' className='text-xs'>
                  {item.dose}
                </Badge>
              </div>

              <div className='space-y-2'>
                <p className='text-sm text-neutral-600 dark:text-neutral-400'>
                  <span className='font-medium'>Instructions:</span> {item.sig}
                </p>
                <div className='flex flex-wrap items-center gap-3 text-xs text-neutral-500'>
                  <span>
                    <span className='font-medium text-neutral-600'>Unit:</span>{' '}
                    {medicationUnits[item.unit_of_measure as keyof typeof medicationUnits] || item.unit_of_measure}
                  </span>
                  {item.quantity_authorized != null && (
                    <span>
                      <span className='font-medium text-neutral-600'>Qty:</span>{' '}
                      {item.quantity_authorized}
                    </span>
                  )}
                  {(item.day_supply != null || item.days_supply != null) && (
                    <span>
                      <span className='font-medium text-neutral-600'>Days Supply:</span>{' '}
                      {item.day_supply ?? item.days_supply}
                    </span>
                  )}
                </div>
              </div>
            </div>

            <div className='flex flex-col items-end space-y-3'>
              <div className='text-right'>
                {(() => {
                  const qty = Number(item.quantity_authorized) || 1;
                  const total = item.cartPrice * qty;
                  const discountCents = Number(item.discount_cents) || 0;
                  const discountDollars = discountCents / 100;
                  return (
                    <>
                      <div className='text-xl font-bold text-neutral-900 dark:text-neutral-100'>
                        ${(total - discountDollars).toFixed(2)}
                      </div>
                      {qty > 1 && (
                        <div className='text-xs text-neutral-400'>
                          ${item.cartPrice.toFixed(2)} × {qty}
                        </div>
                      )}
                      {discountDollars > 0 && (
                        <div className='text-xs font-medium text-green-600'>
                          -${discountDollars.toFixed(2)} discount
                        </div>
                      )}
                    </>
                  );
                })()}
              </div>

              <div className='flex items-center space-x-2'>
                {/* <Button
                  variant="outline"
                  size="sm"
                  onClick={() => handleQuantityChange(-1)}
                  className="h-8 w-8 p-0"
                >
                  <Minus className="h-3 w-3" />
                </Button> */}
                {/* <span className="w-12 text-center font-medium">{item.quantity_authorized}</span> */}
                {/* <Button
                  variant="outline"
                  size="sm"
                  onClick={() => handleQuantityChange(1)}
                  className="h-8 w-8 p-0"
                >
                  <Plus className="h-3 w-3" />
                </Button> */}
              </div>
            </div>
          </div>
        </CardContent>
      </Card>
    </motion.div>
  );
};


// Success Component
const CheckoutSuccess: React.FC<{
  transactionId: string;
  onContinue: () => void;
  isSubscription?: boolean;
  subscriptionFrequency?: string;
}> = ({ transactionId, onContinue, isSubscription, subscriptionFrequency }) => (
  <motion.div
    initial={{ opacity: 0, scale: 0.8 }}
    animate={{ opacity: 1, scale: 1 }}
    className='py-16 text-center'
  >
    <CheckCircle className='mx-auto mb-6 h-24 w-24 text-green-500' />
    <h2 className='mb-4 text-3xl font-bold text-neutral-900 dark:text-neutral-100'>
      Payment Successful!
    </h2>
    <p className='mx-auto mb-8 max-w-md text-neutral-600 dark:text-neutral-400'>
      {isSubscription
        ? `Your auto-refill is set up! Your medications will be delivered ${subscriptionFrequency}.`
        : "Your prescription has been processed successfully. You'll receive updates on your order status."}
    </p>

    <div className='space-y-4'>
      <div className='mx-auto max-w-md rounded-lg border border-green-200 bg-green-50 p-4'>
        <div className='flex items-center space-x-2 text-green-700'>
          <Receipt className='h-4 w-4' />
          <span className='font-medium'>Transaction ID: {transactionId}</span>
        </div>
      </div>

      {isSubscription && (
        <div className='mx-auto max-w-md rounded-lg border border-primary/20 bg-primary/5 p-4'>
          <div className='flex items-center justify-center space-x-2 text-primary'>
            <RefreshCw className='h-4 w-4' />
            <span className='font-medium'>Auto-Refill Active</span>
          </div>
          <p className='mt-1 text-sm text-neutral-600'>
            Next delivery{' '}
            {subscriptionFrequency === 'weekly'
              ? 'in 1 week'
              : subscriptionFrequency === 'monthly'
                ? 'in 1 month'
                : 'in 3 months'}
          </p>
        </div>
      )}
      {/*Payment completed  You may close this window now */}
      {
        <div className='mx-auto max-w-md rounded-lg border border-green-200 bg-green-50 p-4'>
          <div className='flex items-center space-x-2 text-green-700'>
            <CheckCircle className='h-4 w-4' />
            <span className='font-medium'>Payment completed. You may close this window now.</span>
          </div>
        </div>
      }
    </div>
  </motion.div>
);

// Main PrescriptionCart Component
const PrescriptionCart: React.FC<PrescriptionCartProps> = ({ patient, onBack }) => {
  const { items, metadata, cartCount, cartTotal, subtotalBeforeDiscount, totalDiscountDollars, isLoading, clearPrescriptionCart } =
    usePrescriptionCart();
  const { setClinic, clinic, isAdmin } = useClinic();
  const { token } = useParams();
  const stripeAccountId = clinic?.paymentAccount?.stripe_account_id
  const lastViewedLinkId = useRef<string | null>(null);

  const navigate = useNavigate();
  const [currentStep, setCurrentStep] = useState<CheckoutStep>(CheckoutStep.REVIEW);
  const [checkoutResponse, setCheckoutResponse] = useState<any>(null);
  const [error, setError] = useState<string | null>(null);

  // Address state
  const [isEditingAddress, setIsEditingAddress] = useState(false);
  const [isSavingAddress, setIsSavingAddress] = useState(false);
  const [addressData, setAddressData] = useState<AddressData>({
    addressLine1: '',
    addressLine2: '',
    city: '',
    state: '',
    postalCode: '',
  });

  // Derive the default address from the patient object
  const defaultAddress = patient?.addresses?.find((a: any) => a.is_default) || patient?.addresses?.[0];

  // Initialize address data from patient's default address
  useEffect(() => {
    if (defaultAddress) {
      setAddressData({
        addressLine1: defaultAddress.line_1 || '',
        addressLine2: defaultAddress.line_2 || '',
        city: defaultAddress.city || '',
        state: defaultAddress.stateInfo?.code || '',
        postalCode: defaultAddress.postal_code || '',
      });
    }
  }, [defaultAddress?.line_1, defaultAddress?.city, defaultAddress?.postal_code]);

  // Subscription state
  const [isSubscription, setIsSubscription] = useState(false);
  const [subscriptionFrequency, setSubscriptionFrequency] = useState<
    'weekly' | 'monthly' | 'quarterly'
  >('monthly');

  // ============================================
  // SHIPPING STATE
  // ============================================
  /** Available shipping carriers fetched from API */
  const [carriers, setCarriers] = useState<ShippingCarrier[]>([]);
  /** Currently selected carrier ID */
  const [selectedCarrierId, setSelectedCarrierId] = useState<string>('');
  /** Available delivery options for selected carrier */
  const [shippingOptions, setShippingOptions] = useState<ShippingOption[]>([]);
  /** Currently selected shipping option ID */
  const [selectedShippingOptionId, setSelectedShippingOptionId] = useState<string>('');
  /** Loading state for carriers fetch */
  const [isLoadingCarriers, setIsLoadingCarriers] = useState(false);
  /** Loading state for shipping options fetch */
  const [isLoadingShippingOptions, setIsLoadingShippingOptions] = useState(false);
  /** Whether patient qualifies for free shipment */
  const [isFreeShipment, setIsFreeShipment] = useState(false);
  /** Type of free shipment: 'global' or 'clinic_specific' */
  const [freeShipmentType, setFreeShipmentType] = useState<string | null>(null);
  /** Patient's state name for display purposes */
  const [patientState, setPatientState] = useState<string | null>(null);
  /** Package dimensions used for shipping rate calculation */
  const [packageDimensions, setPackageDimensions] = useState<PackageDimensions | null>(null);
  /** Current shipping cost in dollars */
  const [shippingCostDollars, setShippingCostDollars] = useState(0);
  /** Original shipping cost before free shipment discount */
  const [shippingOriginalCostDollars, setShippingOriginalCostDollars] = useState(0);
  /** Error message for shipping options (shown inline) */
  const [shippingError, setShippingError] = useState<string | null>(null);
  /** Whether this order requires cold chain (overnight/express) shipping */
  const [requiresColdChain, setRequiresColdChain] = useState(false);

  // Fetch full prescription data
  const { data: prescriptionData } = useQuery({
    queryKey: ['prescription-link', token],
    queryFn: () => prescriptionService.getPrescriptionLink(token!),
    enabled: !!token,
  });

  // Extract prescription for easy access
  const prescription = prescriptionData?.data?.prescription;
  
  // Check if shipping is selected (either pre-filled or user-selected)
  // External fulfillment (OptioRx) handles its own shipping — skip requirement
  const isExternalFulfillment = !!prescription?.is_external_fulfillment;
  const hasShipping = isExternalFulfillment || (!!selectedCarrierId && !!selectedShippingOptionId);
  
  // Get selected carrier and option names for display
  const selectedCarrier = carriers.find(c => c.id === selectedCarrierId);
  const selectedOption = shippingOptions.find(o => o.id === selectedShippingOptionId);
  
  // Calculate total with shipping
  const totalWithShipping = cartTotal + shippingCostDollars;

  const handleBack = () => {
    if (onBack) {
      onBack();
    } else {
      navigate('/storefront');
    }
  };

  /**
   * Gets the current shipping selection object
   * @returns {ShippingSelection | null} The shipping selection or null if not fully selected
   */
  const getShippingSelection = (): ShippingSelection | null => {
    if (!selectedCarrierId || !selectedShippingOptionId) return null;
    
    const carrier = carriers.find(c => c.id === selectedCarrierId);
    const option = shippingOptions.find(o => o.id === selectedShippingOptionId);
    
    if (!carrier || !option) return null;
    
    return {
      carrierId: carrier.id,
      carrierName: carrier.name,
      optionId: option.id,
      optionName: option.name,
      cost: option.totalCost,
      originalCost: option.originalCost,
      isFreeShipment,
      freeShipmentType,
    };
  };

  /**
   * Fetches available shipping carriers from the API
   */
  const fetchCarriers = async () => {
    if (!clinic?.id) return;
    
    setIsLoadingCarriers(true);
    try {
      const result = await shippingService.getCarriers(clinic.id);
      if (result.success && result.data) {
        setCarriers(result.data);
      } else {
        console.error('Failed to fetch carriers:', result.error);
      }
    } catch (error) {
      console.error('Error fetching carriers:', error);
    } finally {
      setIsLoadingCarriers(false);
    }
  };

  /**
   * Handles carrier selection and fetches shipping options for the selected carrier
   * @param {string} carrierId - The selected carrier ID
   */
  const handleCarrierChange = async (carrierId: string) => {
    setSelectedCarrierId(carrierId);
    setSelectedShippingOptionId('');
    setShippingOptions([]);
    setIsFreeShipment(false);
    setFreeShipmentType(null);
    setShippingCostDollars(0);
    setShippingOriginalCostDollars(0);
    setShippingError(null);
    setPackageDimensions(null);
    setRequiresColdChain(false);

    if (!carrierId || !clinic?.id || !patient?.id) return;

    setIsLoadingShippingOptions(true);
    try {
      const result = await shippingService.calculateShippingCost(
        clinic.id,
        patient.id,
        carrierId,
        prescriptionMedicationRequestsToShippingLines(items)
      );

      if (result.success && result.data) {
        setShippingOptions(result.data);
        setIsFreeShipment(result.isFreeShipment);
        setFreeShipmentType(result.freeShipmentType || null);
        setPatientState(result.patientState || null);
        setPackageDimensions(result.packageDimensions ?? null);
        setRequiresColdChain(result.requiresColdChain);
        setShippingError(null);

        // Auto-select first option if available
        if (result.data.length > 0) {
          handleShippingOptionChange(result.data[0].id, result.data);
        }
      } else {
        // Show error inline instead of toast
        setShippingError(result.error || 'Failed to calculate shipping cost. Please try a different carrier.');
      }
    } catch (error) {
      console.error('Error calculating shipping cost:', error);
      setShippingError('Failed to load shipping options. Please try again.');
    } finally {
      setIsLoadingShippingOptions(false);
    }
  };

  /**
   * Handles shipping option selection and updates the shipping cost
   * @param {string} optionId - The selected shipping option ID
   * @param {ShippingOption[]} options - Optional shipping options array (used for auto-selection)
   */
  const handleShippingOptionChange = (optionId: string, options?: ShippingOption[]) => {
    setSelectedShippingOptionId(optionId);
    
    const optionsToSearch = options || shippingOptions;
    const selectedOption = optionsToSearch.find(o => o.id === optionId);
    
    if (selectedOption) {
      setShippingCostDollars(selectedOption.totalCost);
      setShippingOriginalCostDollars(selectedOption.originalCost);
    } else {
      setShippingCostDollars(0);
      setShippingOriginalCostDollars(0);
    }
  };

  const handleProceedToPayment = () => {
    // Validate shipping selection is required
    if (!hasShipping) {
      toast({
        title: 'Shipping Required',
        description: 'Please select a shipping carrier and delivery option before proceeding.',
        variant: 'destructive',
      });
      return;
    }
    setCurrentStep(CheckoutStep.PAYMENT);
  };

  const handleStripePayment = async (paymentMethodId: string) => {
    if (!prescriptionData?.data?.prescription) {
      setError('Missing prescription data')
      return
    }

    const prescription = prescriptionData.data.prescription
    const clinicId = prescription.clinic_id || patient.clinic_id || clinic?.id

    if (!clinicId) {
      setError('Clinic information is missing. Please try again.')
      setCurrentStep(CheckoutStep.ERROR)
      return
    }

    setCurrentStep(CheckoutStep.PROCESSING)

    try {
      if (!hasShipping) {
        toast({
          title: 'Shipping Required',
          description: 'Please select a shipping carrier and delivery option.',
          variant: 'destructive',
        })
        setCurrentStep(CheckoutStep.PAYMENT)
        return
      }

      const shippingSelection = getShippingSelection()
      if (shippingSelection) {
        const saveResult = await shippingService.saveShippingSelection(prescription.id, shippingSelection)
        if (!saveResult.success) {
          toast({
            title: 'Shipping Warning',
            description: saveResult.error || 'Unable to save shipping selection',
            duration: 5000,
          })
        }
      }

      const requestPayload = {
        prescription_id: prescription.id,
        clinic_id: clinicId,
        amount: totalWithShipping,
        payment_method_id: paymentMethodId,
        is_subscription: isSubscription,
        subscription_frequency: isSubscription ? subscriptionFrequency : undefined,
        subscription_auto_renew: isSubscription ? true : undefined,
        shipping: shippingSelection
          ? {
              carrier_id: shippingSelection.carrierId,
              carrier_name: shippingSelection.carrierName,
              service_id: shippingSelection.optionId,
              service_name: shippingSelection.optionName,
              cost_cents: Math.round(shippingSelection.cost * 100),
              original_cost_cents: Math.round(shippingSelection.originalCost * 100),
              is_free_shipment: shippingSelection.isFreeShipment,
              free_shipment_type: shippingSelection.freeShipmentType,
            }
          : undefined,
      }

      let paymentResult = await processPayment(requestPayload)

      if (paymentResult.error?.requires_action && paymentResult.error?.client_secret) {
        const connectedAccountId = clinic?.paymentAccount?.stripe_account_id
        if (!connectedAccountId) {
          throw new Error('Stripe account is not configured for this clinic')
        }

        const stripe = await getStripeInstance(connectedAccountId)
        if (!stripe) {
          throw new Error('Unable to initialize Stripe')
        }

        const { error: nextActionError } = await stripe.handleNextAction({
          clientSecret: paymentResult.error.client_secret,
        })
        if (nextActionError) {
          throw new Error(nextActionError.message || 'Authentication failed')
        }

        paymentResult = await processPayment(requestPayload)
      }

      if (!paymentResult.success) {
        throw new Error(paymentResult.error?.message || 'Payment processing failed')
      }

      try {
        await prescriptionService.submitPrescription(prescription.id)
      } catch (submitError: unknown) {
        const errorMessage =
          submitError instanceof Error
            ? submitError.message
            : 'Order submission to pharmacy failed. Our team will process it manually.'

        toast({
          title: 'Payment successful',
          description: `Order submission failed: ${errorMessage}`,
          variant: 'default',
          duration: 8000,
        })
      }

      if (token) {
        await prescriptionService.markPrescriptionLinkAsUsed(
          prescriptionData.data.prescriptionLink.id
        )
      }

      setCheckoutResponse({ transaction: paymentResult.transaction })
      setCurrentStep(CheckoutStep.SUCCESS)

      localStorage.setItem('prescription-checkout-success', 'true')

      toast({
        title: 'Payment successful!',
        description: isSubscription
          ? `Auto-refill enabled with ${subscriptionFrequency} delivery.`
          : 'Your prescription has been processed successfully.',
        duration: 5000,
      })
    } catch (error: unknown) {
      const errorMsg =
        error instanceof Error
          ? error.message
          : typeof error === 'object' && error !== null && 'message' in error
            ? String((error as { message: unknown }).message)
            : 'Payment processing failed'
      setError(errorMsg)
      setCurrentStep(CheckoutStep.ERROR)

      if (isStripeOnboardingRequiredError(error)) {
        toast({
          ...STRIPE_ONBOARDING_TOAST,
          variant: 'destructive',
          duration: 8000,
          ...(isAdmin ? { action: (
            <ToastAction
              altText="Go to Payments"
              onClick={() => navigate('/payments?tab=settings')}
            >
              Go to Payments
            </ToastAction>
          ) } : {}),
        })
      } else {
        toast({
          title: 'Payment failed',
          description: errorMsg,
          variant: 'destructive',
          duration: 5000,
        })
      }
    }
  }

  const handlePaymentError = (errorMessage: string) => {
    console.error('errorMessage', errorMessage);
    setError(errorMessage);
    setCurrentStep(CheckoutStep.ERROR);
  };

  const handleContinue = () => {
    // Clear the prescription cart after successful checkout
    clearPrescriptionCart();
    navigate('/storefront');
  };

  const handleRetry = () => {
    setError(null);
    setCurrentStep(CheckoutStep.PAYMENT);
  };

  const handleSaveAddress = async () => {
    if (!addressData.addressLine1 || !addressData.city || !addressData.state || !addressData.postalCode) {
      toast({
        title: 'Missing address fields',
        description: 'Please fill in all required address fields.',
        variant: 'destructive',
      });
      return;
    }

    if (!token) return;

    setIsSavingAddress(true);
    try {
      const result = await prescriptionService.updatePrescriptionLinkAddress(token, {
        line_1: addressData.addressLine1,
        line_2: addressData.addressLine2 || undefined,
        city: addressData.city,
        state_code: addressData.state,
        postal_code: addressData.postalCode,
      });

      if (result.success) {
        // Update the patient object in-memory so the display refreshes
        if (patient.addresses && patient.addresses.length > 0) {
          const addr = patient.addresses.find((a: any) => a.is_default) || patient.addresses[0];
          if (addr) {
            addr.line_1 = addressData.addressLine1;
            addr.line_2 = addressData.addressLine2;
            addr.city = addressData.city;
            addr.stateInfo = result.data?.address?.stateInfo || addr.stateInfo;
            addr.postal_code = addressData.postalCode;
          }
        } else {
          // Patient had no addresses, add the new one
          patient.addresses = [result.data?.address];
        }

        setIsEditingAddress(false);
        toast({
          title: 'Address updated',
          description: 'Your shipping address has been saved.',
          duration: 3000,
        });

        // Re-fetch shipping options with the new address
        if (selectedCarrierId && clinic?.id && patient?.id) {
          handleCarrierChange(selectedCarrierId);
        }
      } else {
        toast({
          title: 'Error',
          description: result.error?.message || 'Failed to update address.',
          variant: 'destructive',
        });
      }
    } catch (error: any) {
      toast({
        title: 'Error',
        description: error?.message || 'Failed to update address.',
        variant: 'destructive',
      });
    } finally {
      setIsSavingAddress(false);
    }
  };

  useEffect(() => {
    if (patient?.clinic) {
      setClinic(patient.clinic);
    }
  }, [patient?.clinic]);

  useEffect(() => {
    const link = prescriptionData?.data?.prescriptionLink;
    if (!link?.id || link.viewed_at) return;
    if (lastViewedLinkId.current === link.id) return;

    lastViewedLinkId.current = link.id;
    prescriptionService.markPrescriptionLinkAsViewed(link.id).catch(error => {
      console.error('Failed to mark prescription link as viewed:', error);
    });
  }, [prescriptionData?.data?.prescriptionLink]);

  // Fetch carriers when clinic is available
  useEffect(() => {
    if (clinic?.id) {
      fetchCarriers();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [clinic?.id]);

  // Pre-fill shipping from prescription data if available
  useEffect(() => {
    if (prescription && carriers.length > 0) {
      const prefilledCarrierId = prescription.shipping_carrier_id;
      const prefilledServiceId = prescription.shipping_service_id;
      
      if (prefilledCarrierId && !selectedCarrierId) {
        // Set the carrier and trigger loading shipping options
        setSelectedCarrierId(prefilledCarrierId);
        
        // Fetch shipping options for pre-filled carrier
        const prefillShippingOptions = async () => {
          if (!clinic?.id || !patient?.id) return;
          
          setIsLoadingShippingOptions(true);
          try {
            const result = await shippingService.calculateShippingCost(
              clinic.id,
              patient.id,
              prefilledCarrierId,
              prescriptionMedicationRequestsToShippingLines(items)
            );

            if (result.success && result.data) {
              setShippingOptions(result.data);
              setIsFreeShipment(result.isFreeShipment);
              setFreeShipmentType(result.freeShipmentType || null);
              setPatientState(result.patientState || null);
              setPackageDimensions(result.packageDimensions ?? null);
              setRequiresColdChain(result.requiresColdChain);
              
              // Pre-select the service if it was saved
              if (prefilledServiceId) {
                const prefilledOption = result.data.find(o => o.id === prefilledServiceId);
                if (prefilledOption) {
                  setSelectedShippingOptionId(prefilledServiceId);
                  setShippingCostDollars(prefilledOption.totalCost);
                  setShippingOriginalCostDollars(prefilledOption.originalCost);
                } else if (result.data.length > 0) {
                  // Fallback to first option if pre-filled option not found
                  handleShippingOptionChange(result.data[0].id, result.data);
                }
              } else if (result.data.length > 0) {
                // Auto-select first option if no pre-filled service
                handleShippingOptionChange(result.data[0].id, result.data);
              }
            }
          } catch (error) {
            console.error('Error pre-filling shipping options:', error);
          } finally {
            setIsLoadingShippingOptions(false);
          }
        };
        
        prefillShippingOptions();
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [prescription, carriers, clinic?.id, patient?.id]);

  if (isLoading) {
    return (
      <div className='min-h-screen bg-neutral-50 dark:bg-neutral-900'>
        <div className='container mx-auto px-4 py-8'>
          <div className='mb-8'>
            <div className='mb-4 h-8 w-32 animate-pulse rounded bg-neutral-200 dark:bg-neutral-700'></div>
            <div className='h-4 w-48 animate-pulse rounded bg-neutral-200 dark:bg-neutral-700'></div>
          </div>
          <div className='space-y-4'>
            {[...Array(3)].map((_, i) => (
              <PrescriptionItemSkeleton key={i} />
            ))}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className='min-h-screen bg-neutral-50 dark:bg-neutral-900 overflow-auto h-screen'>
      <div className='container mx-auto px-4 py-8'>
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          className='mb-8'
        >
          {/* <Button variant="ghost" onClick={handleBack} className="mb-4">
            <ArrowLeft className="h-4 w-4 mr-2" />
            Exit
          </Button> */}
          <h1 className='mb-2 text-3xl font-bold text-neutral-900 dark:text-neutral-100'>
            Prescription for {patient.full_name}
          </h1>
          <p className='text-neutral-600 dark:text-neutral-400'>
            This order is based on a prescription from your practitioner.
          </p>
        </motion.div>

        {items.length === 0 ? (
          // Empty State
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            className='py-16 text-center'
          >
            <Package className='mx-auto mb-6 h-24 w-24 text-neutral-400' />
            <h2 className='mb-4 text-2xl font-semibold text-neutral-900 dark:text-neutral-100'>
              No medications found
            </h2>
            <p className='mx-auto mb-8 max-w-md text-neutral-600 dark:text-neutral-400'>
              There are no medications associated with this prescription link.
            </p>
            <Button onClick={handleBack} size='lg'>
              Go Back
            </Button>
          </motion.div>
        ) : (
          <div className='grid grid-cols-1 gap-8 lg:grid-cols-3'>
            {/* Main Content */}
            <div className='lg:col-span-2'>
              <AnimatePresence mode='sync'>
                {currentStep === CheckoutStep.REVIEW && (
                  <motion.div
                    key='review'
                    initial={{ opacity: 0, x: -20 }}
                    animate={{ opacity: 1, x: 0 }}
                    exit={{ opacity: 0, x: 20 }}
                    className='space-y-6'
                  >
                    {/* Patient Info */}
                    <Card>
                      <CardHeader>
                        <CardTitle className='flex items-center space-x-2'>
                          <User className='h-5 w-5' />
                          <span>Patient Information</span>
                        </CardTitle>
                      </CardHeader>
                      <CardContent>
                        <div className='space-y-2'>
                          <div className='flex items-center space-x-2'>
                            <span className='font-medium'>Name:</span>
                            <span>{patient.full_name}</span>
                          </div>
                          {patient.email && (
                            <div className='flex items-center space-x-2'>
                              <span className='font-medium'>Email:</span>
                              <span>{patient.email}</span>
                            </div>
                          )}
                          {patient.dob && (
                            <div className='flex items-center space-x-2'>
                              <span className='font-medium'>Date of Birth:</span>
                              <span>{formatDOB(patient.dob)}</span>
                            </div>
                          )}
                        </div>
                      </CardContent>
                    </Card>

                    {/* Shipping Address */}
                    <Card>
                      <CardHeader>
                        <CardTitle className='flex items-center justify-between'>
                          <div className='flex items-center space-x-2'>
                            <MapPin className='h-5 w-5' />
                            <span>Shipping Address</span>
                          </div>
                          {!isEditingAddress && (
                            <Button
                              variant='ghost'
                              size='sm'
                              onClick={() => {
                                // Pre-fill form with current address data
                                if (defaultAddress) {
                                  setAddressData({
                                    addressLine1: defaultAddress.line_1 || '',
                                    addressLine2: defaultAddress.line_2 || '',
                                    city: defaultAddress.city || '',
                                    state: defaultAddress.stateInfo?.code || '',
                                    postalCode: defaultAddress.postal_code || '',
                                  });
                                }
                                setIsEditingAddress(true);
                              }}
                              className='text-sm text-primary'
                            >
                              <Pencil className='mr-1 h-3.5 w-3.5' />
                              {defaultAddress ? 'Edit' : 'Add Address'}
                            </Button>
                          )}
                        </CardTitle>
                      </CardHeader>
                      <CardContent>
                        {isEditingAddress ? (
                          <div className='space-y-4'>
                            <AddressInput
                              value={addressData}
                              onChange={setAddressData}
                            />
                            <div className='flex items-center gap-2 pt-2'>
                              <Button
                                onClick={handleSaveAddress}
                                disabled={isSavingAddress}
                                size='sm'
                              >
                                {isSavingAddress ? (
                                  <>
                                    <Loader2 className='mr-2 h-3.5 w-3.5 animate-spin' />
                                    Saving...
                                  </>
                                ) : (
                                  'Save Address'
                                )}
                              </Button>
                              <Button
                                variant='outline'
                                size='sm'
                                onClick={() => setIsEditingAddress(false)}
                                disabled={isSavingAddress}
                              >
                                Cancel
                              </Button>
                            </div>
                          </div>
                        ) : defaultAddress ? (
                          <div className='text-sm text-neutral-700 dark:text-neutral-300'>
                            <p>{defaultAddress.line_1}</p>
                            {defaultAddress.line_2 && <p>{defaultAddress.line_2}</p>}
                            <p>
                              {defaultAddress.city}, {defaultAddress.stateInfo?.code || defaultAddress.state}{' '}
                              {defaultAddress.postal_code}
                            </p>
                          </div>
                        ) : (
                          <div className='flex items-start space-x-2 p-3 bg-amber-50 border border-amber-200 rounded-lg'>
                            <AlertCircle className='h-4 w-4 text-amber-600 mt-0.5 flex-shrink-0' />
                            <div>
                              <p className='text-sm text-amber-700'>
                                No shipping address on file. Please add an address to continue.
                              </p>
                              <Button
                                variant='link'
                                size='sm'
                                onClick={() => setIsEditingAddress(true)}
                                className='h-auto p-0 text-amber-800 underline'
                              >
                                Add shipping address
                              </Button>
                            </div>
                          </div>
                        )}
                      </CardContent>
                    </Card>

                    {/* Medications */}
                    <div>
                      <h2 className='mb-4 text-xl font-semibold'>Prescribed Medications</h2>
                      <div className='space-y-4'>
                        {items.map((item, index) => (
                          <PrescriptionItemCard key={item.id} item={item} index={index} />
                        ))}
                      </div>
                    </div>

                    {/* Shipping Selection — hidden for external fulfillment (e.g. OptioRx) */}
                    {!isExternalFulfillment && (
                    <Card>
                      <CardHeader>
                        <CardTitle className='flex items-center space-x-2'>
                          <Truck className='h-5 w-5' />
                          <span>Shipping Options</span>
                        </CardTitle>
                      </CardHeader>
                      <CardContent className='space-y-4'>
                        {/* Free Shipment Banner */}
                        {isFreeShipment && patientState && (
                          <div className='flex items-center gap-2 p-3 bg-green-50 border border-green-200 rounded-lg'>
                            <Gift className='h-5 w-5 text-green-600 flex-shrink-0' />
                            <div>
                              <p className='text-sm font-medium text-green-800'>
                                Free Shipping Available!
                              </p>
                              <p className='text-xs text-green-600'>
                                You qualify for free shipping to {patientState}
                              </p>
                            </div>
                          </div>
                        )}

                        {/* Carrier Selection */}
                        <div className='space-y-2'>
                          <Label className='text-sm font-medium'>
                            Shipping Carrier <span className='text-red-500'>*</span>
                          </Label>
                          <Select
                            value={selectedCarrierId}
                            onValueChange={handleCarrierChange}
                            disabled={isLoadingCarriers}
                          >
                            <SelectTrigger className='w-full'>
                              <SelectValue
                                placeholder={
                                  isLoadingCarriers ? 'Loading carriers...' : 'Select a carrier'
                                }
                              />
                            </SelectTrigger>
                            <SelectContent>
                              {carriers.map(carrier => (
                                <SelectItem key={carrier.id} value={carrier.id}>
                                  <div className='flex items-center gap-2'>
                                    <Truck className='h-4 w-4 text-neutral-500' />
                                    {carrier.name}
                                  </div>
                                </SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        </div>

                        {/* Cold Chain Shipping Banner */}
                        {requiresColdChain && (
                          <div className='flex items-center gap-2 rounded-lg border border-blue-200 bg-blue-50 p-3'>
                            <Snowflake className='h-5 w-5 flex-shrink-0 text-blue-600' />
                            <div>
                              <p className='text-sm font-medium text-blue-800'>
                                Cold Chain Shipping Required
                              </p>
                              <p className='text-xs text-blue-600'>
                                This order contains temperature-sensitive medication. Only overnight/express options are shown.
                              </p>
                            </div>
                          </div>
                        )}

                        {/* Delivery Options */}
                        {selectedCarrierId && (
                          <div className='space-y-2'>
                            <Label className='text-sm font-medium'>
                              Delivery Option <span className='text-red-500'>*</span>
                            </Label>
                            {isLoadingShippingOptions ? (
                              <div className='flex items-center justify-center py-4'>
                                <Loader2 className='h-5 w-5 animate-spin text-primary' />
                                <span className='ml-2 text-sm text-neutral-500'>
                                  Loading options...
                                </span>
                              </div>
                            ) : shippingOptions.length > 0 ? (
                              <RadioGroup
                                value={selectedShippingOptionId}
                                onValueChange={optionId => handleShippingOptionChange(optionId)}
                                className='space-y-2'
                              >
                                {shippingOptions.map(option => (
                                  <div
                                    key={option.id}
                                    className={`flex items-center space-x-3 p-3 border rounded-lg transition-colors cursor-pointer ${
                                      selectedShippingOptionId === option.id
                                        ? 'border-primary bg-primary/5'
                                        : 'border-neutral-200 hover:bg-neutral-50'
                                    }`}
                                  >
                                    <RadioGroupItem
                                      value={option.id}
                                      id={`shipping-${option.id}`}
                                    />
                                    <Label
                                      htmlFor={`shipping-${option.id}`}
                                      className='flex-1 cursor-pointer flex justify-between items-center'
                                    >
                                      <span className='text-sm font-medium'>{option.name}</span>
                                      <div className='flex items-center gap-2'>
                                        {isFreeShipment && option.totalCost === 0 ? (
                                          <>
                                            <span className='text-sm text-neutral-400 line-through'>
                                              ${option.originalCost.toFixed(2)}
                                            </span>
                                            <Badge
                                              variant='secondary'
                                              className='bg-green-100 text-green-700 hover:bg-green-100'
                                            >
                                              FREE
                                            </Badge>
                                          </>
                                        ) : (
                                          <span className='text-sm font-semibold'>
                                            ${option.totalCost.toFixed(2)}
                                          </span>
                                        )}
                                      </div>
                                    </Label>
                                  </div>
                                ))}
                              </RadioGroup>
                            ) : shippingError ? (
                              <div className='flex items-start space-x-2 p-3 bg-red-50 border border-red-200 rounded-lg'>
                                <AlertCircle className='h-4 w-4 text-red-600 mt-0.5 flex-shrink-0' />
                                <span className='text-sm text-red-700'>{shippingError}</span>
                              </div>
                            ) : (
                              <p className='text-sm text-neutral-500 py-2'>
                                No delivery options available for this carrier
                              </p>
                            )}
                          </div>
                        )}

                        {/* Package Dimensions */}
                        {packageDimensions && (
                          <div className='flex items-center gap-2 p-3 bg-neutral-50 border border-neutral-200 rounded-lg'>
                            <Package className='h-4 w-4 text-neutral-500 flex-shrink-0' />
                            <span className='text-sm text-neutral-600'>
                              Package: {packageDimensions.length}" × {packageDimensions.width}" × {packageDimensions.height}"
                            </span>
                          </div>
                        )}

                        {/* No shipping selected warning */}
                        {!hasShipping && !isLoadingCarriers && carriers.length > 0 && (
                          <div className='flex items-start space-x-2 p-3 bg-amber-50 border border-amber-200 rounded-lg'>
                            <AlertCircle className='h-4 w-4 text-amber-600 mt-0.5 flex-shrink-0' />
                            <span className='text-sm text-amber-700'>
                              Please select a shipping carrier and delivery option to continue.
                            </span>
                          </div>
                        )}
                      </CardContent>
                    </Card>
                    )}

                    {/* Subscription Option */}
                    <Card className='border-primary/20 bg-primary/5'>
                      <CardHeader className='pb-3'>
                        <CardTitle className='flex items-center justify-between'>
                          <div className='flex items-center space-x-2'>
                            <RefreshCw className='h-5 w-5 text-primary' />
                            <span>Subscribe & Save</span>
                          </div>
                          <Switch checked={isSubscription} onCheckedChange={setIsSubscription} />
                        </CardTitle>
                      </CardHeader>
                      <CardContent className='space-y-4'>
                        <p className='text-sm text-neutral-600 dark:text-neutral-400'>
                          Get your medications delivered automatically on your schedule. No need to
                          reorder.
                        </p>

                        {isSubscription && (
                          <motion.div
                            initial={{ opacity: 0, height: 0 }}
                            animate={{ opacity: 1, height: 'auto' }}
                            exit={{ opacity: 0, height: 0 }}
                            className='space-y-4 pt-2'
                          >
                            <div className='space-y-2'>
                              <Label className='flex items-center gap-2'>
                                <Calendar className='h-4 w-4 text-neutral-500' />
                                Delivery Schedule
                              </Label>
                              <Select
                                value={subscriptionFrequency}
                                onValueChange={(value: 'weekly' | 'monthly' | 'quarterly') =>
                                  setSubscriptionFrequency(value)
                                }
                              >
                                <SelectTrigger className='w-full'>
                                  <SelectValue placeholder='Select frequency' />
                                </SelectTrigger>
                                <SelectContent>
                                  <SelectItem value='weekly'>Weekly</SelectItem>
                                  <SelectItem value='monthly'>Monthly</SelectItem>
                                  <SelectItem value='quarterly'>Every 3 Months</SelectItem>
                                </SelectContent>
                              </Select>
                            </div>
                          </motion.div>
                        )}
                      </CardContent>
                    </Card>
                  </motion.div>
                )}

                {currentStep === CheckoutStep.PAYMENT && (
                  <motion.div
                    key='payment'
                    initial={{ opacity: 0, x: -20 }}
                    animate={{ opacity: 1, x: 0 }}
                    exit={{ opacity: 0, x: 20 }}
                  >
                    {stripeAccountId ? (
                        <StripePaymentForm
                          connectedAccountId={stripeAccountId}
                          amountCents={Math.round(totalWithShipping * 100)}
                          onPaymentMethodCreated={handleStripePayment}
                          onError={(err: string) => handlePaymentError(err)}
                          disabled={!hasShipping}
                        />
                      ) : (
                        <Card className='border border-destructive/20'>
                          <CardContent className='p-4 text-sm text-destructive'>
                            Stripe account is not configured for this clinic.
                          </CardContent>
                        </Card>
                      )}
                  </motion.div>
                )}

                {currentStep === CheckoutStep.PROCESSING && (
                  <motion.div
                    key='processing'
                    initial={{ opacity: 0, scale: 0.8 }}
                    animate={{ opacity: 1, scale: 1 }}
                    className='py-16 text-center'
                  >
                    <Loader2 className='mx-auto mb-4 h-16 w-16 animate-spin text-primary' />
                    <h2 className='mb-2 text-2xl font-semibold'>Processing Payment</h2>
                    <p className='text-gray-600'>
                      Please wait while we process your payment for this prescription...
                    </p>
                    <div className='mt-4 text-sm text-gray-500'>
                      Do not refresh or close this page
                    </div>
                  </motion.div>
                )}

                {currentStep === CheckoutStep.SUCCESS && checkoutResponse && (
                  <CheckoutSuccess
                    transactionId={checkoutResponse.transaction?.id || 'Unknown'}
                    onContinue={handleContinue}
                    isSubscription={isSubscription}
                    subscriptionFrequency={subscriptionFrequency}
                  />
                )}

                {currentStep === CheckoutStep.ERROR && (
                  <motion.div
                    key='error'
                    initial={{ opacity: 0, scale: 0.8 }}
                    animate={{ opacity: 1, scale: 1 }}
                    className='py-16 text-center'
                  >
                    {error?.toLowerCase().includes('already has a payment') ? (
                      <>
                        <CheckCircle className='mx-auto mb-4 h-16 w-16 text-green-500' />
                        <h2 className='mb-2 text-2xl font-semibold'>Payment Already Completed</h2>
                        <p className='mb-6 text-gray-600'>
                          This prescription has already been paid for. No further action is needed.
                        </p>
                        <Button onClick={handleBack} variant='default'>
                          Go Back
                        </Button>
                      </>
                    ) : (
                      <>
                        <AlertCircle className='mx-auto mb-4 h-16 w-16 text-red-500' />
                        <h2 className='mb-2 text-2xl font-semibold'>Payment Failed</h2>
                        <p className='mb-6 text-gray-600'>
                          {error || 'There was an error processing your payment.'}
                        </p>
                        <div className='space-x-4'>
                          <Button onClick={handleRetry} variant='default'>
                            Try Again
                          </Button>
                          <Button onClick={handleBack} variant='outline'>
                            Go Back
                          </Button>
                        </div>
                      </>
                    )}
                  </motion.div>
                )}
              </AnimatePresence>
            </div>

            {/* Order Summary */}
            <div className='lg:col-span-1'>
              <motion.div
                initial={{ opacity: 0, x: 20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: 0.3 }}
              >
                <Card className='sticky top-8'>
                  <CardHeader>
                    <CardTitle>Order Summary</CardTitle>
                  </CardHeader>
                  <CardContent className='space-y-4'>
                    <div className='flex justify-between text-sm'>
                      <span>Items ({cartCount})</span>
                      <span>${subtotalBeforeDiscount.toFixed(2)}</span>
                    </div>

                    {totalDiscountDollars > 0 && (
                      <div className='flex justify-between text-sm'>
                        <span className='text-green-600'>Discount</span>
                        <span className='font-medium text-green-600'>-${totalDiscountDollars.toFixed(2)}</span>
                      </div>
                    )}

                    <div className='flex justify-between text-sm'>
                      <span>Processing Fee</span>
                      <span className='text-green-600'>Free</span>
                    </div>

                    {/* Shipping Information */}
                    {isExternalFulfillment ? (
                      <div className='flex justify-between text-sm'>
                        <span className='flex items-center gap-1'>
                          <Truck className='h-3 w-3' />
                          Shipping
                        </span>
                        <span className='text-neutral-400 text-xs'>Not Applicable</span>
                      </div>
                    ) : hasShipping ? (
                      <div className='space-y-2'>
                        <div className='flex justify-between text-sm'>
                          <span className='flex items-center gap-1'>
                            <Truck className='h-3 w-3' />
                            Shipping
                          </span>
                          {isFreeShipment ? (
                            <div className='flex items-center gap-2'>
                              <span className='text-xs text-neutral-400 line-through'>
                                ${shippingOriginalCostDollars.toFixed(2)}
                              </span>
                              <span className='text-green-600 font-medium'>Free</span>
                            </div>
                          ) : (
                            <span>${shippingCostDollars.toFixed(2)}</span>
                          )}
                        </div>
                        <div className='text-xs text-neutral-500'>
                          {selectedCarrier?.name} - {selectedOption?.name}
                        </div>
                        {isFreeShipment && (
                          <div className='flex items-center gap-1 text-xs text-green-600'>
                            <Gift className='h-3 w-3' />
                            <span>Free shipping applied!</span>
                          </div>
                        )}
                      </div>
                    ) : (
                      <div className='flex justify-between text-sm'>
                        <span className='flex items-center gap-1'>
                          <Truck className='h-3 w-3' />
                          Shipping
                        </span>
                        <span className='text-neutral-400 text-xs'>Select above</span>
                      </div>
                    )}

                    {isSubscription && (
                      <div className='space-y-2 rounded-lg bg-primary/5 p-3'>
                        <div className='flex items-center gap-2 text-sm font-medium text-primary'>
                          <RefreshCw className='h-4 w-4' />
                          <span>Auto-Refill Enabled</span>
                        </div>
                        <div className='text-xs capitalize text-neutral-600'>
                          {subscriptionFrequency} delivery
                        </div>
                      </div>
                    )}

                    <Separator />
                    <div className='flex justify-between text-lg font-semibold'>
                      <span>{isSubscription ? 'Total per refill' : 'Total'}</span>
                      <span>${totalWithShipping.toFixed(2)}</span>
                    </div>

                    {currentStep === CheckoutStep.REVIEW && (
                      <Button
                        onClick={handleProceedToPayment}
                        className='w-full'
                        size='lg'
                        disabled={cartCount === 0 || !hasShipping}
                      >
                        <CreditCard className='mr-2 h-5 w-5' />
                        Proceed to Payment
                      </Button>
                    )}

                    {currentStep === CheckoutStep.PAYMENT && (
                      <Button
                        onClick={() => setCurrentStep(CheckoutStep.REVIEW)}
                        variant='outline'
                        className='w-full'
                        size='lg'
                      >
                        <ArrowLeft className='mr-2 h-5 w-5' />
                        Back to Review
                      </Button>
                    )}

                    <div className='text-center text-xs text-neutral-500'>
                      <div className='flex items-center justify-center space-x-2'>
                        <ShieldCheck className='h-3 w-3' />
                        <span>Secure prescription processing</span>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              </motion.div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default PrescriptionCart;
